// rounds/public-catalog hooks
// TanStack Query (useQuery/useMutation) + client-state hooks only.
// Pure API functions belong in services/. Keys come from ../_shared/keys.ts — never array literals.
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { STALE_TIME } from "@core/libs/react-query";
import { i18n } from "@core/libs/i18n";
import { toLocale } from "@core/locale";
import { isApiError } from "@core/errors";
import { publicRoundKeys } from "@modules/rounds/_shared";
import { getPublicRound, listPublicRounds } from "../services";
import { toPreviewGroups, type PublicRoundPreview } from "../types";

/**
 * The public round catalogue.
 *
 * Deliberately stateless next to `useRounds()` — no search, no status filter, no page. The public
 * endpoint accepts none of them and returns the whole visible catalogue in one response, so there
 * is nothing for a filter to key on and no page for a visitor to be stranded on.
 *
 * `serverNow` is returned alongside the rounds because it is half of every deadline the screen
 * renders — the countdown helper takes it explicitly so no component can reach for `Date.now()`.
 */
export function usePublicRounds() {
  const query = useQuery({
    queryKey: publicRoundKeys.lists(),
    queryFn: listPublicRounds,
    // Operational: an applicant refreshing during the last hour of a round must see it close.
    staleTime: STALE_TIME.operational,
    // Nothing to gate on — this is the one anonymous read in the app. There is no session to
    // wait for and no id to require, so an `enabled` here could only ever be `true`.
    enabled: true,
  });

  return {
    query,
    rounds: query.data?.rounds ?? [],
    /** Null until the first response — the countdowns render only once the clock has arrived. */
    serverNow: query.data?.serverNow ?? null,
    /**
     * No rounds at all. Unlike the staff list there is no "your filters matched nothing" case to
     * distinguish, so an empty catalogue has exactly one meaning: nothing is open right now.
     */
    isEmpty: query.isSuccess && (query.data?.rounds.length ?? 0) === 0,
  };
}

/**
 * Query options for ONE publicly visible round, shared by the route guard and the screen.
 *
 * Factored out as `queryOptions` rather than written twice on purpose: the guard resolves the round
 * in `beforeLoad` and the screen then reads it, and they must hit the SAME cache entry or every
 * navigation costs two identical requests and can render a round the guard never approved. This is
 * the same shape `@core/auth` uses for `meQueryOptions`.
 */
export function publicRoundDetailQueryOptions(roundId: string, locale: string) {
  return queryOptions({
    queryKey: publicRoundKeys.detail(roundId, locale),
    queryFn: () => getPublicRound(roundId, locale),
    // Operational, not reference: the frozen requirements barely change, but
    // `accepting_applications` turns false at the deadline and this payload gates entry to the
    // funnel. A long tier would let a closed round keep admitting applicants.
    staleTime: STALE_TIME.operational,
    // A 404 is a verdict, not a blip — the round is invisible to the public and retrying cannot
    // change that. Retrying would only delay the guard's redirect.
    retry: false,
  });
}

/**
 * Guard-friendly resolve: the round, or `null` when it cannot be shown to the public.
 *
 * Deliberately the same contract as `@core/auth`'s `tryGetProfile` — `null` means "redirect", not
 * "something exploded". Only `ApiError`s collapse to `null` (404 invisible, 503 snapshot missing,
 * 429 throttled); a `DataContractError` propagates, so a contract break surfaces loudly instead of
 * masquerading as a missing round.
 *
 * Reads the active language itself rather than taking it as an argument: `beforeLoad` is not React,
 * so there is no `useTranslation` to hand it one, and every caller would otherwise reach for the
 * i18next singleton independently.
 */
export async function ensurePublicRound(
  queryClient: QueryClient,
  roundId: string
): Promise<PublicRoundPreview | null> {
  const locale = toLocale(i18n.resolvedLanguage ?? "fr");
  try {
    return await queryClient.ensureQueryData(
      publicRoundDetailQueryOptions(roundId, locale)
    );
  } catch (error) {
    if (isApiError(error)) return null;
    throw error;
  }
}

/**
 * One publicly visible round, inside React.
 *
 * Keyed on the ACTIVE language, so switching language refetches the localized eligibility and
 * document copy instead of leaving the previous language's text on screen.
 */
export function usePublicRound(roundId: string) {
  const { i18n: instance } = useTranslation();
  const locale = toLocale(instance.resolvedLanguage ?? "fr");

  const query = useQuery({
    ...publicRoundDetailQueryOptions(roundId, locale),
    // The id comes from a route param that the guard already resolved, so it is always present —
    // but stated rather than assumed, per the explicit-`enabled` rule.
    enabled: roundId !== "",
  });

  const schema = query.data?.schema ?? null;

  return {
    query,
    round: query.data?.round ?? null,
    /** Eligibility + documents, or null while the round's configuration is not yet frozen. */
    requirements: query.data?.round.requirements ?? null,
    /**
     * The timeline, active steps only and in the round's own order.
     *
     * Filtering `is_active` here rather than in the component because an inactive milestone is not
     * a step the applicant is waiting for — it is a row the administrator switched off, and US-E11-04
     * says the page states what happens next, not what was once configured.
     */
    milestones: (query.data?.round.milestones ?? [])
      .filter((milestone) => milestone.is_active)
      .sort((a, b) => a.display_order - b.display_order),
    /**
     * The four dossier groups, or `null` when the round has not published its configuration.
     *
     * `null` and `[]` mean different things and the screen renders them differently: `null` is
     * "nothing to show yet" (a Scheduled round), `[]` would be "this round asks nothing", which
     * cannot happen — the fixed fields are always there.
     */
    groups: schema === null ? null : toPreviewGroups(schema),
  };
}
